// BuildChat — Studio chat-only mode of a Live Motion Build (spec §5). Continues the
// console conversation (same reasoning-agent thread `reasoning-<draftId>`), listens
// to the build job on the root socket namespace (subscribe_job, events replayed on
// join), streams build progress into the right-hand preview panel, imports the
// training on every finished scene, reveals the editor on the first one (storyboard
// spec §5.4) and feeds the scene-list ghosts. Chat state lives server-side
// (LangGraph checkpoints) — nothing persisted here.
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AssistantRuntimeProvider,
  fromThreadMessageLike,
  makeAssistantToolUI,
  MessagePrimitive,
  useMessage,
} from "@assistant-ui/react";
import { useAgUiRuntime } from "@assistant-ui/react-ag-ui";
import { HttpAgent } from "@ag-ui/client";
import { io } from "socket.io-client";
import i18n from "../../../locales/i18n";
import "./chat.css";
import { FOCUS_RING, Thread } from "./ChatPanel";
import BuildNotice, { pauseErrorKey } from "./BuildNotice";
import { SendIcon } from "./icons";
import { agUiFetch } from "./agUiFetch";
import {
  fetchActiveBuild,
  fetchAgentFlows,
  postBuildChat,
  postBuildContinue,
  postBuildStop,
} from "./buildApi";
import BuildPauseReply, { optId } from "./BuildPauseReply";
import { authedFetch } from "../../../common/http_service";
import { approveJob } from "../../../common/server_utils.es6";
import { getToken, whenEnvironmentReady } from "../../../common/environment_config";
import { bearerSocketAuth } from "../../utils/socketAuth";
import { seekEditorPlayhead } from "../../../common/playhead_tracker";
import {
  AGENT_PHASES,
  BUILD_STEPS,
  buildEnded,
  buildProgress,
  formatElapsed,
  heroUrl,
  resolveExpired,
  setBuildGhosts,
  setBuildNotice,
  setPauseFrozen,
  stepChecklist,
  truncateWords,
  useBuildLocked,
  useBuildNotice,
} from "./buildMode";
import { renderMiniMarkdown } from "./miniMarkdown";
import InsufficientBalanceModal from "../common/modal/InsufficientBalanceModal";
import { briefFromState, CourseBriefPanel, modelsFromState, recipeFromState } from "./BuildBrief";
import {
  buildStages,
  MagicWait,
  MindmapButton,
  StageStepper,
  useMindmapReady,
  waitPanels,
  SceneStrip,
} from "./BuildWaiting";
import MindmapPanel from "../carousel/audio/MindmapPanel";

const EVENTS = [
  "course_build_started",
  "course_build_progress",
  "course_build_scene_ready",
  "course_build_scene_failed",
  "course_build_done",
  "course_build_step",
];
const COMPLETE = { type: "complete", reason: "unknown" };
const REVEAL_MS = 900;
const SUB_RETRY_LINE = 5; // after this many failed subscribes the user sees a line
const TOKEN_POLLS = 60; // no token at mount: re-check once a second for a minute
const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const root = () =>
  String(window.ideaLibraryRootUrl || "")
    .replace(/\/+$/, "")
    .replace(/\/api$/, "");
const reducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const initialBuildFeed = {
  total: 0,
  planned: [],
  ready: [],
  failed: [],
  done: false,
  items: [],
  live: null,
  saves: 0,
};

// mw subscription_error: only an ownership/expiry refusal is final; anything else
// (Redis hiccup, replay failure) is retried.
export const isExpiredSubscription = (e) => !!e && e.error === "Access denied";
export const retryDelay = (attempt) => Math.min(1000 * 2 ** (attempt - 1), 30000);
// Scenes not imported yet (`seen` = Set of navIds already imported; scenes become ready in any order).
// Never anything for an expired job with no saved scene: importing would reveal (and later autosave) the empty course doc.
export const newlyReady = (feed, seen) =>
  feed.ready.filter((r) => !seen.has(r.navId)).map((r) => r.navId);

// reasoning-agent 409 {error:'chat_closed'} → a normal assistant turn with the
// localized line instead of the generic HTTP error (AG-UI SSE, RUN_STARTED first).
export function chatClosedSse(body, text) {
  let input = {};
  try {
    input = JSON.parse(body) || {};
  } catch (e) {
    /* keep defaults */
  }
  const ids = {
    threadId: String(input.threadId || ""),
    runId: String(input.runId || "chat-closed"),
  };
  const messageId = `chat-closed-${ids.runId}`;
  return [
    { type: "RUN_STARTED", ...ids },
    { type: "TEXT_MESSAGE_START", messageId, role: "assistant" },
    { type: "TEXT_MESSAGE_CONTENT", messageId, delta: text },
    { type: "TEXT_MESSAGE_END", messageId },
    { type: "RUN_FINISHED", ...ids },
  ]
    .map((e) => `data: ${JSON.stringify(e)}\n\n`)
    .join("");
}

async function buildAgentFetch(url, init) {
  const res = await agUiFetch(url, init);
  if (res.status !== 409) return res;
  const body = await res
    .clone()
    .json()
    .catch(() => ({}));
  if (!body || body.error !== "chat_closed") return res;
  return new Response(chatClosedSse(init && init.body, i18n.t("aideaChat.build.chatClosed")), {
    status: 200,
    headers: { "Content-Type": "text/event-stream" },
  });
}

const once = (state, kind) =>
  state.items.some((i) => i.kind === kind)
    ? state
    : { ...state, items: [...state.items, { kind, key: kind }] };

const STOP_KEYS = {
  "team.budget_exceeded": "stoppedBudget",
  hold_failed: "stoppedHold",
  user: "stoppedByUser",
};
// Why a build built nothing (mw `stopped`); unknown/absent → noSceneGeneric.
const NO_SCENE_KEYS = { tts_unavailable: "noSceneVoice", agent_unavailable: "noSceneAgent" };

// Parallel builds interleave scenes and finish them in any order: every scene keeps its own live line
// (state.lives) and the one shown (state.live) is the lowest unfinished scene's. None unfinished: unchanged.
const withLive = (state) => {
  const open = Object.keys(state.lives)
    .map(Number)
    .filter((i) => !state.failed.includes(i) && !state.ready.some((r) => r.index === i));
  return open.length ? { ...state, live: state.lives[Math.min(...open)] } : state;
};
const freshLive = (index) => ({ index, step: null, text: "", imageUrls: [], notes: [] });

export function buildFeedReducer(state, action) {
  if (action.type === "expired") {
    if (state.done) return state;
    const item = { kind: "expired", key: "expired", empty: state.ready.length === 0 };
    return { ...state, done: true, items: [...state.items, item] };
  }
  // R8 past the 24 h job TTL: the job is gone but /active (DB) still has the paused build; the card is built from it.
  if (action.type === "pausedRestore") {
    const d = action.data || {};
    return {
      ...state,
      total: d.total || state.total,
      done: true,
      paused: true,
      remainingCredits: Number(d.remainingCredits) || 0,
      items: [
        ...state.items.filter((i) => i.kind !== "expired"),
        { kind: "paused", key: "paused", built: d.built },
      ],
    };
  }
  if (action.type === "connIssue" || action.type === "chatClosed") return once(state, action.type);
  if (action.type !== "event") return state;
  const d = action.data || {};
  if (action.event === "course_build_started") {
    return {
      ...state,
      total: d.total || state.total,
      planned: Array.isArray(d.plannedScenes) ? d.plannedScenes : state.planned,
    };
  }
  if (action.event === "course_build_progress") {
    // Each scene keeps its own preview; the lowest unfinished one is shown.
    const lives = state.lives || {};
    const prev = lives[d.index] || freshLive(d.index);
    return withLive({
      ...state,
      total: d.total || state.total,
      lives: {
        ...lives,
        [d.index]: {
          ...prev,
          step: d.step || prev.step,
          phase: undefined, // saving step shows instead of a stale agent phase
          text: d.text || prev.text,
          imageUrls: Array.isArray(d.imageUrls) ? d.imageUrls.slice(0, 12) : prev.imageUrls,
          notes: d.note && !prev.notes.includes(d.note) ? [...prev.notes, d.note] : prev.notes,
        },
      },
    });
  }
  if (action.event === "course_build_step") {
    // mw scene agent: a save re-imports the training (effect in BuildChat), no feed item;
    // any other phase becomes the live status line of the scene in flight.
    if (d.saved) return { ...state, saves: (state.saves || 0) + 1 };
    if (!AGENT_PHASES.includes(d.phase)) return state;
    const index = typeof d.index === "number" ? d.index : state.live ? state.live.index : 0;
    const lives = state.lives || {};
    return withLive({
      ...state,
      lives: { ...lives, [index]: { ...(lives[index] || freshLive(index)), phase: d.phase } },
    });
  }
  if (action.event === "course_build_scene_ready") {
    if (state.ready.some((r) => r.index === d.index)) return state;
    const live =
      state.live && state.live.index === d.index ? { ...state.live, phase: undefined } : state.live;
    return withLive({
      ...state,
      live,
      lives: state.lives || {},
      total: d.total || state.total,
      ready: [...state.ready, { index: d.index, navId: d.navId }].sort((a, b) => a.index - b.index),
      items: [
        ...state.items,
        { kind: "ready", key: `r${d.index}`, index: d.index, navId: d.navId, total: d.total },
      ],
    });
  }
  if (action.event === "course_build_scene_failed") {
    if (state.failed.includes(d.index)) return state;
    return withLive({
      ...state,
      lives: state.lives || {},
      total: d.total || state.total,
      failed: [...state.failed, d.index],
      items: [...state.items, { kind: "failed", key: `f${d.index}`, index: d.index }],
    });
  }
  if (action.event === "course_build_done") {
    if (state.done) return state;
    // R8: paused after scene 1 — the build is not over: no unlock, the notice offers Continue / Improve.
    if (d.paused) {
      return {
        ...state,
        total: d.total || state.total,
        done: true,
        paused: true,
        remainingCredits: Number(d.remainingCredits) || 0,
        items: [...state.items, { kind: "paused", key: "paused", built: d.built }],
      };
    }
    const items = [
      ...state.items,
      { kind: d.built ? "done" : "none", key: "done", built: d.built, stopped: d.stopped },
    ];
    // Stopped for credits after some scenes: the summary AND the balance bubble.
    if (d.built && d.stopped === "insufficient.balance")
      items.push({ kind: "balance", key: "balance" });
    if (d.built && STOP_KEYS[d.stopped])
      items.push({ kind: "stopNote", key: "stopNote", stopped: d.stopped });
    return { ...state, total: d.total || state.total, done: true, items };
  }
  return state;
}

// Notice for the standard ChatPanel once the editor is revealed: frozen (with live
// progress) while building, then ready or the stop reason.
export function buildNoticeFor(feed) {
  const t = (k, o) => i18n.t(`aideaChat.${k}`, o);
  if (feed.paused) {
    const p = feed.items.find((i) => i.kind === "paused");
    const built = p && typeof p.built === "number" ? p.built : feed.ready.length; // a restored pause has no replayed scenes
    const count = Math.max(0, feed.total - built);
    return {
      title: t("buildNotice.paused"),
      line: t("buildNotice.pausedLine", { count, credits: feed.remainingCredits }),
      pause: true,
    };
  }
  if (!feed.done) {
    const n = feed.ready.length;
    return {
      title: t("buildNotice.frozen"),
      line: n ? t("buildNotice.progress", { n, total: feed.total }) : null,
    };
  }
  const d = feed.items.find((i) => i.kind === "done" || i.kind === "none");
  const stopKey =
    d && (d.stopped === "insufficient.balance" ? "stoppedBalance" : STOP_KEYS[d.stopped]);
  // Nothing built: never "ready" (the editor is empty) — a stop notice with the reason, kept until the next message.
  if (d && d.kind === "none") {
    const line = stopKey
      ? t(`build.${stopKey}`)
      : t(`buildNotice.${NO_SCENE_KEYS[d.stopped] || "noSceneGeneric"}`);
    return { title: t("buildNotice.noScene"), line, stop: true };
  }
  // scenes: ChatPanel turns a counted ready notice into the first message of a fresh studio thread.
  return {
    title: stopKey ? t(`build.${stopKey}`) : t("buildNotice.ready"),
    line: null,
    ready: !stopKey,
    stop: !!stopKey,
    scenes: (d && d.built) || 0,
  };
}

// What an assistant turn shows: its text bubble, only its previewScene card (the one
// tool with a UI here), or nothing (empty / tool-only turns left a bare avatar row).
export function assistantView(parts) {
  const list = parts || [];
  if (list.some((p) => p && p.type === "text" && typeof p.text === "string" && p.text.trim()))
    return "text";
  return list.some((p) => p && p.type === "tool-call" && p.toolName === "previewScene")
    ? "tool"
    : null;
}

const MarkdownText = ({ text }) => <>{renderMiniMarkdown(text)}</>;
const PART_COMPONENTS = { Text: MarkdownText };

function BuildAssistantMessage() {
  const message = useMessage();
  const view = assistantView(message && message.content);
  if (!view) return null;
  if (view === "tool") {
    return (
      <MessagePrimitive.Root className="tw-py-2">
        <MessagePrimitive.Parts components={PART_COMPONENTS} />
      </MessagePrimitive.Root>
    );
  }
  return (
    <MessagePrimitive.Root className="tw-py-2">
      <div className="tw-min-w-0 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-text)] tw-whitespace-pre-wrap tw-break-words">
        <MessagePrimitive.Parts components={PART_COMPONENTS} />
      </div>
    </MessagePrimitive.Root>
  );
}

const Spinner = () => <span className="aidea-build-spinner" aria-hidden="true" />;
const knownStep = (live) => !!live && BUILD_STEPS.includes(live.step);

// Scene-agent phase line (course_build_step); null for a stream build, which shows its step.
export const agentPhaseLine = (live, t) =>
  live && live.phase ? t(`phase.${live.phase}`, { n: live.index + 1 }) : null;

// Stop control under the build status: in-app confirm (never window.confirm), then POST /stop. The stopped state
// arrives with the ended event (course_build_done {stopped:"user"}); until then the build runs out its current scene.
export function StopBuild() {
  const t = (k) => i18n.t(`aideaChat.build.${k}`);
  const [step, setStep] = useState("idle"); // idle → confirm → stopping | failed
  const stop = async () => {
    setStep("stopping");
    try {
      await postBuildStop(window.idealStudioTrainingId);
    } catch (e) {
      setStep("failed");
    }
  };
  if (step === "stopping") return <div className="aidea-build-status-hint">{t("stopping")}</div>;
  if (step === "confirm")
    return (
      <div className="aidea-build-status-row" role="alertdialog" aria-label={t("stopConfirm")}>
        <span>{t("stopConfirm")}</span>
        <button type="button" className="aidea-build-btn" onClick={stop}>
          {t("stopYes")}
        </button>
        <button
          type="button"
          className="aidea-build-btn aidea-build-btn-ghost"
          onClick={() => setStep("idle")}
        >
          {t("stopNo")}
        </button>
      </div>
    );
  return (
    <div className="aidea-build-status-row">
      {step === "failed" && <span>{t("stopFailed")}</span>}
      <button
        type="button"
        className="aidea-build-btn aidea-build-btn-ghost"
        onClick={() => setStep("confirm")}
      >
        {t("stop")}
      </button>
    </div>
  );
}

// Pinned under the chat while building: what runs now + time on this scene. Own tick so
// the chat runtime does not re-render every second.
function BuildStatus({ live }) {
  const t = (k, o) => i18n.t(`aideaChat.build.${k}`, o);
  const index = live ? live.index : -1;
  const [start, setStart] = useState(() => Date.now());
  const [now, setNow] = useState(start);
  useEffect(() => {
    const s = Date.now();
    setStart(s);
    setNow(s);
  }, [index]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="aidea-build-status">
      <Spinner />
      <div className="aidea-build-status-body">
        <div className="aidea-build-status-row">
          <span aria-live="polite">
            {agentPhaseLine(live, t) ||
              (knownStep(live)
                ? t("status", { n: index + 1, step: t(`stepName.${live.step}`) })
                : t("step.waiting"))}
          </span>
          <span className="aidea-build-status-time">{formatElapsed(now - start)}</span>
        </div>
        <div className="aidea-build-status-hint">{t("hint")}</div>
        <StopBuild />
      </div>
    </div>
  );
}

// previewScene (reasoning-agent tool) → seek the open editor once; "Show again" repeats.
function PreviewSceneCard({ args, navRef }) {
  const navId = (navRef.current || [])[(Number(args && args.sceneNumber) || 1) - 1];
  const sec = Number(args && args.atSec) || 0;
  const done = useRef(false);
  const show = useCallback(() => navId && seekEditorPlayhead(navId, sec), [navId, sec]);
  useEffect(() => {
    if (!done.current && navId) {
      done.current = true;
      show();
    }
  }, [navId, show]);
  if (!navId) return null;
  return (
    <button type="button" className="aidea-build-btn" onClick={show}>
      {i18n.t("aideaChat.build.showAgain", { n: args.sceneNumber })}
    </button>
  );
}

function FeedItem({ item, total, onPreview, onBalance }) {
  const t = (k, o) => i18n.t(`aideaChat.build.${k}`, o);
  const balance =
    item.kind === "balance" || (item.kind === "none" && item.stopped === "insufficient.balance");
  const text =
    item.kind === "paused"
      ? i18n.t("aideaChat.buildNotice.paused")
      : item.kind === "ready"
        ? t("sceneReady", { n: item.index + 1, total: item.total || total })
        : item.kind === "failed"
          ? t("sceneFailed", { n: item.index + 1 })
          : item.kind === "done"
            ? t("done", { count: item.built })
            : balance
              ? t("stoppedBalance")
              : STOP_KEYS[item.stopped] && (item.kind === "none" || item.kind === "stopNote")
                ? t(STOP_KEYS[item.stopped])
                : item.kind === "none" || item.kind === "connIssue" || item.kind === "chatClosed"
                  ? t(item.kind)
                  : t(item.empty ? "expiredEmpty" : "expired");
  const button =
    item.kind === "ready"
      ? { label: t("preview"), run: () => onPreview(item.navId) }
      : item.kind === "done" || (item.kind === "expired" && !item.empty)
        ? { label: t("watch"), run: () => onPreview(null) }
        : balance
          ? { label: t("balanceCta"), run: onBalance }
          : null;
  return (
    <div className="aidea-build-feed-item">
      <span>{text}</span>
      {button && (
        <button type="button" className="aidea-build-btn" onClick={button.run}>
          {button.label}
        </button>
      )}
    </div>
  );
}

// Reveal storyboard (spec §5.4). Editor chrome lives outside this React tree, so it is
// animated with the Web Animations API: toolbar from the top, scene list from the left,
// timeline from the bottom, and the canvas grows out of the last preview image (FLIP).
// Each slide ends on the element's own computed transform, so inline transforms survive.
export function revealEditorChrome(fromRect, doc = document) {
  const view = doc.defaultView || window;
  const slide = (selector, from, delay) => {
    const el = doc.querySelector(selector);
    if (!el || !el.animate) return;
    const own = view.getComputedStyle(el).transform;
    el.animate(
      [
        { transform: from, opacity: 0 },
        { transform: own && own !== "none" ? own : "none", opacity: 1 },
      ],
      { duration: 500, delay, easing: EASE, fill: "backwards" }
    );
  };
  slide("#app > .navBar", "translateY(-100%)", 100);
  slide("#leftPanelContainer", "translateX(-100%)", 200);
  slide("#timelineDock", "translateY(100%)", 250);
  const canvas = doc.getElementById("maincontent");
  if (!canvas || !canvas.animate || !fromRect) return;
  const to = canvas.getBoundingClientRect();
  if (!to.width || !to.height) return;
  const dx = fromRect.left - to.left;
  const dy = fromRect.top - to.top;
  canvas.animate(
    [
      {
        transformOrigin: "top left",
        transform: `translate(${dx}px, ${dy}px) scale(${fromRect.width / to.width}, ${fromRect.height / to.height})`,
        borderRadius: "16px",
      },
      { transformOrigin: "top left", transform: "none", borderRadius: "0px" },
    ],
    { duration: 650, delay: 50, easing: EASE, fill: "backwards" }
  );
}

// Last preview image = FLIP origin. Measured BEFORE the preview panel collapses.
function lastPreviewRect() {
  const imgs = document.querySelectorAll(".aidea-build-preview-hero img");
  const img = imgs[imgs.length - 1];
  return img ? img.getBoundingClientRect() : null;
}

// A landed scene's row fades from the ghost look to the real one (same position: the
// ghost sat exactly where the real row appears, see BuildGhosts).
function morphLanded(navIds) {
  if (reducedMotion()) return;
  navIds.forEach((id) => {
    // Scoped lookup: other surfaces (timeline, canvas) may reuse the navId as a DOM id.
    const el = document.querySelector(`.navItemBlock [id="${CSS.escape(id)}"]`);
    const row = el && el.closest(".navItemBlock");
    if (row && row.animate) {
      row.animate(
        [
          { opacity: 0.35, filter: "saturate(0)" },
          { opacity: 1, filter: "none" },
        ],
        {
          duration: 450,
          easing: EASE,
        }
      );
    }
  });
}

// Right-hand live preview (chat-only phase). Everything comes from course_build_*
// events, so a replay after the console redirect or a reload repaints it fully.
function LivePreview({ feed, reduce, brief, flows }) {
  const t = (k, o) => i18n.t(`aideaChat.build.${k}`, o);
  const live = feed.live;
  const imgs = live ? live.imageUrls : [];
  // Manual pick (per scene); without one the hero follows the newest image.
  const [pick, setPick] = useState(null);
  const hero = heroUrl(imgs, pick, live && live.index);
  const stages = buildStages(feed);
  const current = stages.find((s) => s.state === "current");
  const mindmapReady = useMindmapReady(!feed.done);
  const [mindmapOpen, setMindmapOpen] = useState(false);
  const stepLine =
    agentPhaseLine(live, t) ||
    (knownStep(live)
      ? t(`step.${live.step}`, { n: live.index + 1, total: feed.total })
      : t("step.waiting"));
  return (
    <aside className="aidea-build-preview">
      <div className="aidea-build-preview-step">
        {!feed.done && <Spinner />}
        {stepLine}
      </div>
      {knownStep(live) && !live.phase && !feed.done && (
        <ul className="aidea-build-steps">
          {stepChecklist(live.step).map((s) => (
            <li key={s.key} data-state={s.state}>
              {s.state === "done" ? "✓" : s.state === "active" ? <Spinner /> : "◌"}{" "}
              {t(`stepName.${s.key}`)}
            </li>
          ))}
        </ul>
      )}
      <div className="aidea-build-preview-hero">
        {/* No image yet (always so for the scene agent): the magic field, or the mind map opened in place. */}
        {mindmapOpen ? (
          <MindmapPanel />
        ) : (
          !hero && (
            <MagicWait
              headline={current ? i18n.t(`aideaChat.buildWait.stages.${current.id}`) : stepLine}
              reduce={reduce || feed.done}
              stage={current && current.id}
              panels={waitPanels({
                brief,
                index: live ? live.index : 0,
                flows,
                currentFlow: brief && brief.flowId,
              })}
            />
          )
        )}
        <AnimatePresence initial={false}>
          {hero && !mindmapOpen && (
            <motion.img
              key={hero}
              src={hero}
              alt=""
              initial={reduce ? false : { opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.4 }}
            />
          )}
        </AnimatePresence>
      </div>
      <StageStepper
        stages={stages}
        mindmap={
          (mindmapReady || !feed.done) && (
            <MindmapButton
              ready={mindmapReady}
              open={mindmapOpen}
              onToggle={() => setMindmapOpen((o) => !o)}
            />
          )
        }
      />
      {imgs.length > 1 && (
        <div className="aidea-build-preview-thumbs">
          {imgs.map((u, k) => (
            <button
              key={u}
              type="button"
              className="aidea-build-thumb"
              aria-pressed={u === hero}
              aria-label={t("thumb", { n: k + 1 })}
              onClick={() => setPick({ index: live.index, url: u })}
            >
              <motion.img
                src={u}
                alt=""
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: reduce ? 0 : k * 0.12 }}
              />
            </button>
          ))}
        </div>
      )}
      {live && live.text && (
        <blockquote className="aidea-build-preview-text">{truncateWords(live.text)}</blockquote>
      )}
      {live && live.notes.length > 0 && (
        <ul className="aidea-build-preview-notes">
          {live.notes.map((n) => (
            <li key={n}>{t(`note.${n}`)}</li>
          ))}
        </ul>
      )}
      <SceneStrip feed={feed} />
    </aside>
  );
}

// Running build: the recipe instead of the conversation (nothing to steer yet); children = the progress/Stop card.
function RecipePane({ brief, children }) {
  // /state failed or is slow: after a few seconds a neutral line replaces the loader (progress card and Stop stay).
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (brief) return undefined;
    const id = setTimeout(() => setStalled(true), 5000);
    return () => clearTimeout(id);
  }, [brief]);
  return (
    <div className="aidea-build-recipe-pane" data-testid="build-recipe-pane">
      <div className="aidea-build-recipe-scroll aidea-chat-scroll">
        {brief ? (
          <CourseBriefPanel brief={brief} />
        ) : (
          <div className="aidea-build-feed-item">
            {i18n.t(stalled ? "aideaChat.buildBrief.fallback" : "aideaChat.buildBrief.loading")}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// /state → the brief the recipe pane shows (BuildRuntime's onState).
const briefOf = (data) => {
  const flow = data && data.decisions && data.decisions.agentFlow;
  return {
    ...briefFromState(data),
    recipe: recipeFromState(data),
    models: modelsFromState(data),
    flowId: flow && flow.id,
  };
};

// Chat runtime. Mounted only once the session is ready: its history adapter loads
// /state on creation, and an unauthenticated load leaves the chat empty.
function BuildRuntime({ draftId, navRef, onState, children }) {
  const lng = i18n && i18n.language ? String(i18n.language).slice(0, 2) : undefined;
  const headers = useMemo(
    () => ({ "X-Aidea-Training-Id": draftId, ...(lng ? { "X-Aidea-Locale": lng } : {}) }),
    [draftId, lng]
  );
  const threadId = `reasoning-${draftId}`;

  // /state also carries the course brief (R9); a ref keeps the history adapter (and the runtime) stable.
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  const agent = useMemo(
    () =>
      new HttpAgent({
        url: `${root()}/api/doc-planner/reasoning-agent`,
        threadId,
        headers,
        fetch: buildAgentFetch,
      }),
    [threadId, headers]
  );
  const history = useMemo(
    () => ({
      async load() {
        try {
          const r = await authedFetch(
            `${root()}/api/doc-planner/reasoning-agent/state?threadId=${encodeURIComponent(threadId)}`,
            { headers }
          );
          const data = r.ok ? await r.json() : {};
          if (onStateRef.current) onStateRef.current(data);
          const messages = Array.isArray(data.messages) ? data.messages : [];
          return {
            messages: messages.map((m) => ({
              parentId: null,
              message: fromThreadMessageLike(
                { id: m.id, role: m.role, content: m.content },
                m.id,
                COMPLETE
              ),
            })),
          };
        } catch (e) {
          return { messages: [] };
        }
      },
      async append() {}, // the server checkpoints every turn
    }),
    [threadId, headers]
  );
  const runtime = useAgUiRuntime({ agent, adapters: { history } });

  const PreviewSceneToolUI = useMemo(
    () =>
      makeAssistantToolUI({
        toolName: "previewScene",
        render: ({ args }) => <PreviewSceneCard args={args} navRef={navRef} />,
      }),
    [navRef]
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <PreviewSceneToolUI />
      {children}
    </AssistantRuntimeProvider>
  );
}

const NoComposer = () => null;

// Composer under the pause card: while paused, the text is the scene 1 Improve note; while building it is disabled.
function BuildComposer({ paused, onSend }) {
  const t = (k) => i18n.t(`aideaChat.buildNotice.${k}`);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // one Enter = one POST, even before `busy` re-renders
  useEffect(() => {
    if (paused) return;
    inFlight.current = false; // the next pause (new job) opens it again
    setBusy(false);
  }, [paused]);
  const submit = async (e) => {
    e.preventDefault();
    const note = text.trim();
    if (!paused || !note || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setText("");
    if (await onSend(note)) return; // success stays locked until the remount on the new job
    inFlight.current = false;
    setBusy(false);
  };
  const placeholder = t(paused ? "askPlaceholder" : "composerWait");
  return (
    <form className="tw-bg-[var(--ac-thread-bg)] tw-px-3 tw-pb-3 tw-pt-1" onSubmit={submit}>
      <div className="tw-rounded-3xl tw-bg-[var(--ac-surface)] tw-p-2.5 tw-shadow-sm tw-ring-1 tw-ring-[var(--ac-border)] focus-within:tw-ring-[#d1d5db]">
        <textarea
          rows={1}
          maxLength={1000}
          aria-label={placeholder}
          placeholder={placeholder}
          disabled={!paused || busy}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && submit(e)
          }
          className="tw-box-border tw-block tw-max-h-40 tw-min-h-[40px] tw-w-full tw-resize-none tw-border-none tw-bg-transparent tw-px-2 tw-py-1.5 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-fg)] tw-outline-none placeholder:tw-text-[var(--ac-faint)]"
        />
        <div className="tw-flex tw-items-center tw-gap-1">
          <button
            type="submit"
            aria-label={t("send")}
            title={t("send")}
            disabled={!paused || busy || !text.trim()}
            className={`tw-ml-auto tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-[var(--ac-ink)] tw-text-[var(--aidea-on-primary)] tw-transition-opacity tw-duration-200 tw-cursor-pointer hover:tw-opacity-90 disabled:tw-cursor-not-allowed disabled:tw-opacity-30 ${FOCUS_RING}`}
          >
            <SendIcon size={16} />
          </button>
        </div>
      </div>
    </form>
  );
}

// The standard ChatPanel shows this instead of the studio thread while build-locked: the build conversation (same
// reasoning thread + /state history as the pre-reveal view, no greeting), the pinned notice, and the composer.
// Typed messages are local rows (mw keeps no chat turn for them): mw /chat classifies them (question / redo / continue) and
// answers; a rebuild or continue only happens after the user confirms its card. If /chat fails: explicit choices, never a silent rebuild.
const isContinueIntent = (text) => {
  const trimmed = text.trim().toLowerCase();
  return /^(continue|go on|go|next|devam( et)?|devam edelim|sürdür|continuar|seguir)[.!]*$/.test(
    trimmed
  );
};

export function BuildThread({ draftId, navItemsIds = [] }) {
  const navRef = useRef(navItemsIds);
  navRef.current = navItemsIds;
  const readOnly = !useBuildLocked(); // after the build: history only ("Course build" in the rail), nothing to steer
  const notice = useBuildNotice();
  const paused = !!(notice && notice.pause);
  // Running (the frozen notice): the recipe and the progress/Stop card; paused, done, failed or expired: the conversation.
  const running = !readOnly && !!notice && !notice.pause && !notice.stop && !notice.ready;
  const [brief, setBrief] = useState(null);
  const onState = useCallback((data) => setBrief(briefOf(data)), []);
  const [notes, setNotes] = useState([]); // { key, text, phase, reply, options, seeAll, picked, confirm, status, err }
  const seq = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => {
    if (!paused) setPauseFrozen(false); // the build moved on (new job): the pinned pause card is usable again at its next pause
  }, [paused]);
  useEffect(() => () => setPauseFrozen(false), []);
  const patch = (key, p) => setNotes((n) => n.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const send = async (note) => {
    const key = ++seq.current;
    setNotes((n) => [...n, { key, text: note, phase: "thinking" }]);
    if (isContinueIntent(note)) {
      patch(key, {
        phase: "reply",
        reply: i18n.t("aideaChat.buildNotice.continueAsk"),
        confirm: { kind: "continue" },
      });
      return false;
    }
    try {
      // exclude: everything an earlier reply already showed, so "suggest more" gets different options.
      const exclude = notes.flatMap((n) =>
        (n.options || []).map((o) => (o.kind === "style" ? o.styleId : o.slug))
      );
      const r = await postBuildChat(window.idealStudioTrainingId, note, {
        lng: i18n.language,
        exclude,
      });
      patch(key, {
        phase: "reply",
        reply: r.reply,
        options: r.options,
        seeAll: !!r.seeAll || !!(r.options && r.options.length),
        confirm:
          r.intent === "redo"
            ? { kind: "redo", note: r.note || note }
            : r.intent === "continue"
              ? { kind: "continue" }
              : null,
      });
    } catch (e) {
      patch(key, {
        phase: "reply",
        reply: i18n.t("aideaChat.buildNotice.chatFailed"),
        confirm: { kind: "redo", note, fallback: true },
      });
    }
    return false;
  };
  const pickOption = (key, o) => {
    const label = o.label;
    const note =
      o.kind === "format"
        ? `Rebuild scene 1 in the "${label}" visual format.`
        : `Rebuild scene 1 in the "${label}" image style.`;
    const pick =
      o.kind === "format"
        ? { format: o.slug, variant: o.variant || 1 }
        : { style: { styleSource: o.styleSource, styleId: o.styleId } };
    // picked freezes this reply's option cards (the chosen one reads "Selected"); only the new confirm card is live.
    patch(key, { picked: optId(o), confirm: { kind: "redo", note, pick, name: label }, err: null });
  };
  const confirm = async (key) => {
    const row = notes.find((x) => x.key === key);
    if (!row || !row.confirm || inFlight.current || notes[notes.length - 1].key !== key) return; // one click = one POST, even before the re-render; only the newest reply is live
    inFlight.current = true;
    const { kind, note, pick } = row.confirm;
    patch(key, { status: "busy", err: null });
    setPauseFrozen(true);
    try {
      if (kind === "continue") await notice.onContinue();
      else await notice.onImprove(note, pick);
      patch(key, { status: "done" }); // stays until the remount on the new job
    } catch (e) {
      inFlight.current = false;
      setPauseFrozen(false);
      patch(key, { status: "idle", err: pauseErrorKey(e) });
    }
  };
  const tail = notes.map((n) => (
    <React.Fragment key={n.key}>
      <BuildPauseReply
        row={n}
        paused={paused}
        latest={n.key === notes[notes.length - 1].key}
        onOption={pickOption}
        onConfirm={confirm}
        onCancel={(k) => patch(k, { confirm: null, picked: null })}
      />
    </React.Fragment>
  ));
  return (
    <BuildRuntime draftId={draftId} navRef={navRef} onState={onState}>
      {running ? (
        <RecipePane brief={brief}>
          <BuildNotice />
          <StopBuild />
        </RecipePane>
      ) : (
        <Thread
          AssistantMessage={BuildAssistantMessage}
          EmptyState={null}
          Composer={NoComposer}
          viewportTail={tail}
        >
          {!readOnly && <BuildNotice />}
          {!readOnly && <BuildComposer paused={paused} onSend={send} />}
        </Thread>
      )}
    </BuildRuntime>
  );
}

export default function BuildChat({
  params,
  onScenesChanged,
  onBuildFinished,
  onRevealed,
  onJobChanged,
  resumed = false,
  navItemsIds = [],
}) {
  const { draftId, jobId } = params;
  const [feed, dispatch] = useReducer(buildFeedReducer, initialBuildFeed);
  const [phase, setPhase] = useState(resumed ? "docked" : "chat"); // chat → revealing → docked (headless)
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [tokenPolls, setTokenPolls] = useState(0);
  const [envReady, setEnvReady] = useState(false); // ?code= exchange settled
  const [brief, setBrief] = useState(null);
  const [flows, setFlows] = useState([]);
  const locked = useBuildLocked();
  const reduce = useReducedMotion();
  const revealTimers = useRef({ raf: 0, timeout: 0 });
  const importChain = useRef(Promise.resolve());
  const seenReady = useRef(new Set()); // navIds already imported (scenes become ready in any order)
  const saveImportPending = useRef(false); // an import is queued and has not started yet
  const revealed = useRef(resumed); // a remount on a continued job never re-runs the reveal
  const expiredChecked = useRef(false);
  const finalized = useRef(false);
  // navItemsIds changes identity on every editor render — read it through a ref so
  // the tool UI is registered once and its card never remounts (no re-seek).
  const navRef = useRef(navItemsIds);
  navRef.current = navItemsIds;

  const onState = useCallback((data) => setBrief(briefOf(data)), []);

  // "Other Aidea flows" panel: once, after the session exchange (same auth as /state).
  useEffect(() => {
    if (!envReady) return undefined;
    let live = true;
    fetchAgentFlows(draftId, i18n.language ? String(i18n.language).slice(0, 2) : undefined).then(
      (f) => live && setFlows(f)
    );
    return () => {
      live = false;
    };
  }, [envReady, draftId]);

  // R8: Continue / Improve scene 1 → a new job on the same scenes; EditorApp remounts this component on it (key = jobId).
  // A refusal (409) or a lost response may mean the build already moved on (another tab, an earlier click): /active decides.
  // Probe failure (undefined, e.g. network error) is treated like "same job": the card shows the error, no unlock.
  const resume = useCallback(
    async (action, note, pick) => {
      const trainingId = window.idealStudioTrainingId;
      let next;
      try {
        ({ jobId: next } = await postBuildContinue(trainingId, action, note || undefined, pick));
      } catch (e) {
        const active = await fetchActiveBuild(trainingId);
        if (active && active.jobId !== jobId) return onJobChanged && onJobChanged(active.jobId); // continued elsewhere: follow it
        if (active !== null) throw e; // still this paused job (active exists) or probe failed (undefined): the card shows the error line
        // active === null: no build; build is over
        await Promise.resolve(onScenesChanged()).catch(() => undefined); // build over: final doc, then unlock
        onBuildFinished();
        return setBuildNotice(null); // the card's buttons stay locked after a success: drop it
      }
      if (action === "redo") await Promise.resolve(onScenesChanged()).catch(() => undefined); // scene 1 left the doc
      if (onJobChanged) onJobChanged(next);
    },
    [jobId, onJobChanged, onScenesChanged, onBuildFinished]
  );

  useEffect(() => {
    let live = true;
    whenEnvironmentReady().then(() => live && setEnvReady(true));
    return () => {
      live = false;
    };
  }, []);

  // Socket: root namespace, same auth + pattern as useSessionImageJobs.
  // Waits for the session exchange (a stale stored token would get "Access denied").
  // No token after it (not signed in): there is no token-change event, so poll
  // briefly and create the socket once one exists.
  useEffect(() => {
    if (!envReady) return undefined;
    if (!getToken()) {
      if (tokenPolls >= TOKEN_POLLS) return undefined;
      const t = setTimeout(() => setTokenPolls((n) => n + 1), 1000);
      return () => clearTimeout(t);
    }
    const socket = io(root(), {
      transports: ["websocket", "polling"],
      auth: bearerSocketAuth(getToken),
      reconnection: true,
    });
    let attempts = 0;
    let retry = 0;
    const join = () => socket.emit("subscribe_job", { jobId });
    const onData = (u) => {
      if (u && u.jobId === jobId && EVENTS.includes(u.event)) {
        attempts = 0;
        dispatch({ type: "event", event: u.event, data: u.data });
      }
    };
    // ponytail: keeps retrying at the 30 s cap after the line (the socket's own reconnect
    // re-joins too). Only "Access denied" ends the build view.
    const onError = (e) => {
      if (!e || e.jobId !== jobId) return;
      if (isExpiredSubscription(e)) return dispatch({ type: "expired" });
      attempts += 1;
      if (attempts >= SUB_RETRY_LINE) dispatch({ type: "connIssue" });
      clearTimeout(retry);
      retry = setTimeout(join, retryDelay(attempts));
    };
    socket.on("connect", join);
    socket.on("job_data_update", onData);
    socket.on("subscription_error", onError);
    return () => {
      clearTimeout(retry);
      socket.disconnect();
    };
  }, [jobId, tokenPolls, envReady]);

  // Import on every newly ready scene (serialized). First one: reveal storyboard.
  // Later ones: the ghost row morphs into the real row.
  useEffect(() => {
    const landed = newlyReady(feed, seenReady.current);
    if (!landed.length) return;
    landed.forEach((n) => seenReady.current.add(n));
    saveImportPending.current = true; // the ready import also covers any save in this tick
    importChain.current = importChain.current
      .then(() => {
        saveImportPending.current = false;
        return onScenesChanged();
      })
      .then(() => {
        if (revealed.current) return morphLanded(landed);
        revealed.current = true;
        onRevealed && onRevealed();
        if (reducedMotion()) return setPhase("docked");
        const from = lastPreviewRect(); // before the preview panel collapses
        setPhase("revealing"); // body classes flip in the layout effect below
        const timers = revealTimers.current;
        timers.raf = requestAnimationFrame(() => {
          timers.raf = requestAnimationFrame(() => revealEditorChrome(from));
        });
        timers.timeout = setTimeout(() => setPhase("docked"), REVEAL_MS);
      })
      .catch(() => undefined);
  }, [feed, onScenesChanged, onRevealed]);

  // Scene agent (course_build_step saved:true): saves re-import so the open editor shows the
  // scene evolving. Coalesced: one running + at most one queued save import (replay bursts), none when a
  // ready import is already queued. Same serialized chain; no feed item.
  const seenSaves = useRef(0);
  useEffect(() => {
    if (feed.saves === seenSaves.current) return;
    seenSaves.current = feed.saves;
    if (saveImportPending.current) return;
    saveImportPending.current = true;
    importChain.current = importChain.current
      .then(() => {
        saveImportPending.current = false;
        return onScenesChanged();
      })
      .catch(() => undefined);
  }, [feed.saves, onScenesChanged]);
  useEffect(
    () => () => {
      cancelAnimationFrame(revealTimers.current.raf);
      clearTimeout(revealTimers.current.timeout);
    },
    []
  );

  // A done event always finishes (re-import, reveal, unlock); an expired job without scenes
  // keeps the editor hidden and un-imported, so its autosave stays held (never save a blank doc).
  useEffect(() => {
    // R8: keep the lock and the build URL (never onBuildFinished); the pause card carries the next step.
    // Not revealed yet (first import failed, or a pause restored past the job TTL): import, reveal, dock.
    if (feed.paused) {
      if (finalized.current) return;
      finalized.current = true;
      importChain.current = importChain.current.then(() => {
        if (revealed.current) return undefined;
        return Promise.resolve(onScenesChanged())
          .catch(() => undefined)
          .then(() => {
            revealed.current = true;
            onRevealed && onRevealed();
            setPhase("docked");
          });
      });
      return;
    }
    if (feed.done && (seenReady.current.size > 0 || buildEnded(feed))) {
      if (finalized.current) return;
      finalized.current = true;
      // The final doc differs (mw adds quiz/cover after the last scene): import once more, then unlock.
      importChain.current = importChain.current
        .then(() => onScenesChanged())
        .catch(() => undefined)
        .then(() => {
          // Zero built (classic course: template scenes): no scene ever revealed the editor.
          if (!revealed.current) {
            revealed.current = true;
            onRevealed && onRevealed();
            setPhase("docked");
          }
          // Sahneler eğitime aktarıldı → widget'taki build kartı "Onay Bekliyor"da kalmasın.
          approveJob(jobId);
          onBuildFinished();
        });
    } else if (feed.done && !expiredChecked.current) {
      expiredChecked.current = true;
      // A paused build outlives its 24 h job (Redis) but not its DB row: /active first, else the normal expired path.
      const trainingId = window.idealStudioTrainingId;
      importChain.current = importChain.current
        .then(() => fetchActiveBuild(trainingId))
        .then((active) => {
          if (active && active.paused && active.idealStudioTrainingId === trainingId) {
            dispatch({ type: "pausedRestore", data: active });
            return false;
          }
          return resolveExpired(feed, onScenesChanged);
        })
        .then((ok) => {
          if (!ok) return;
          revealed.current = true;
          onRevealed && onRevealed();
          setPhase("docked");
          approveJob(jobId);
          onBuildFinished();
        });
    }
  }, [feed, jobId, onBuildFinished, onScenesChanged, onRevealed]);

  // Scene-list ghosts (S6) — UI-only, cleared on unmount.
  useEffect(() => {
    setBuildGhosts({
      planned: feed.planned,
      failed: feed.failed,
      ready: feed.ready.map((r) => r.index),
      done: feed.done,
      total: feed.total,
      live: feed.live && { index: feed.live.index, step: feed.live.step, phase: feed.live.phase },
    });
  }, [feed.planned, feed.failed, feed.ready, feed.done, feed.total, feed.live]);
  useEffect(() => () => setBuildGhosts({ planned: [], failed: [], done: true }), []);

  // Layout effect: #app must be visible before revealEditorChrome measures the canvas.
  useLayoutEffect(() => {
    document.body.classList.toggle("aidea-build-chat", phase === "chat");
    document.body.classList.toggle("aidea-build-revealing", phase === "revealing");
    document.body.classList.toggle("aidea-building", locked); // canvas + timeline-clip lock (chat.css)
    return () =>
      document.body.classList.remove("aidea-build-chat", "aidea-build-revealing", "aidea-building");
  }, [phase, locked]);

  // After the reveal the standard ChatPanel carries the status (BuildNotice).
  useEffect(() => {
    if (phase === "chat") return;
    const notice = buildNoticeFor(feed);
    setBuildNotice(
      notice.pause
        ? {
            ...notice,
            onContinue: () => resume("continue"),
            onImprove: (note, pick) => resume("redo", note, pick),
          }
        : notice
    );
    // Ready is transient (ChatPanel turns a counted one into a chat message); stop notices clear on the first user message (BuildNotice).
    if (notice.ready) {
      const id = setTimeout(() => setBuildNotice(null), 8000);
      return () => clearTimeout(id);
    }
  }, [phase, feed, resume]);
  useEffect(() => () => setBuildNotice(null), []);

  const preview = (navId) => seekEditorPlayhead(navId || navItemsIds[0], 0);
  const total = feed.total;
  const doneCount = feed.ready.length;

  // Portal: EditorApp mounts this inside #app, which is hidden/animated during the build.
  const noToken = envReady && !getToken();
  if (phase !== "chat") return null;
  const feedList = (feed.items.length > 0 || noToken) && (
    <div className="aidea-build-feed">
      {noToken && <div className="aidea-build-feed-item">{i18n.t("aideaChat.build.noToken")}</div>}
      {feed.items.map((item) => (
        <FeedItem
          key={item.key}
          item={item}
          total={total}
          onPreview={preview}
          onBalance={() => setBalanceOpen(true)}
        />
      ))}
    </div>
  );
  return createPortal(
    <>
      <InsufficientBalanceModal show={balanceOpen} onClose={() => setBalanceOpen(false)} />
      <div className="aidea-chat-root aidea-build-shell aidea-build-chat">
        <div className="aidea-build-columns">
          <div className="aidea-build-panel tw-flex tw-h-full tw-flex-col tw-overflow-hidden">
            <div className="aidea-build-header">
              <div className="aidea-build-header-row">
                <span className="aidea-build-title">
                  {!feed.done && <Spinner />}
                  {feed.done
                    ? i18n.t("aideaChat.build.titleDone")
                    : i18n.t("aideaChat.build.title")}
                  {total ? ` · ${doneCount}/${total}` : ""}
                </span>
              </div>
              <div className="aidea-build-progress">
                <div style={{ width: `${Math.max(4, buildProgress(feed))}%` }} />
              </div>
            </div>
            {envReady ? (
              <BuildRuntime draftId={draftId} navRef={navRef} onState={onState}>
                {/* Running: the recipe instead of the conversation (nothing to steer yet). Ended here (failed, expired,
                    or done before the reveal): the conversation and its composer come back with the outcome. */}
                {feed.done ? (
                  <Thread AssistantMessage={BuildAssistantMessage}>{feedList}</Thread>
                ) : (
                  <RecipePane brief={brief}>
                    {feedList}
                    <BuildStatus live={feed.live} />
                  </RecipePane>
                )}
              </BuildRuntime>
            ) : (
              <div className="aidea-build-feed">
                <div className="aidea-build-feed-item">{i18n.t("aideaChat.build.loadingChat")}</div>
              </div>
            )}
          </div>
          <LivePreview feed={feed} reduce={reduce} brief={brief} flows={flows} />
        </div>
      </div>
    </>,
    document.body
  );
}
